import type { PublicCampaignSchema, WidgetVariant } from "@/types";
import { type CampaignKey } from "@/campaigns/campaign-key";
import { toGameNames } from "@/campaigns/eligible-games";
import type { Tournament } from "./types";
import { isTournamentSubtypeHidden } from "./unsupported-subtypes";

/**
 * Project the type-agnostic `campaigns` stream into the tournament view list.
 * Pure — the store calls it after a `campaigns` push (via `recomputeTournaments`).
 *
 * **Structural only** — the volatile leaderboard rides its own slice
 * (`tournamentLeaderboardsById`), so this list is rebuilt only when a structural
 * field changes, never on a leaderboard tick (the core performance decision — see
 * `./types`). A tournament is included as soon as its `campaigns` tile arrives,
 * even before its leaderboard lands (the board is simply empty until then).
 */
export function assembleTournaments(
  campaignsById: Record<CampaignKey, PublicCampaignSchema>,
  variant: WidgetVariant
): Tournament[] {
  const result: Tournament[] = [];

  for (const campaign of Object.values(campaignsById)) {
    // Tournament-shaped campaigns split across TWO `type` values: "TO" (point/highest-coin/
    // mission tournaments) and "RA" (Races/Speed Races — a distinct, both-live campaign type on
    // the backend, per `common.constants.CAMPAIGN_TYPE_TOURNAMENT_MAPPING`). Which surfaces show
    // RA is the subtype guard's call below, not this type check — don't reintroduce a
    // `type !== "TO"` check here or every surface loses races, the overlay included.
    if (campaign.type !== "TO" && campaign.type !== "RA") continue;
    if (isTournamentSubtypeHidden(campaign.subtype, variant)) continue;
    // A chase ("IM") is the chase module's (`modules/chase`): no leaderboard, a prize ladder on
    // spins. Keep the two mutually exclusive — `resolveCurrentCampaign` takes the first match.
    if (campaign.subtype === "IM") continue;

    const { games, count } = toGameNames(
      campaign.eligible_games,
      campaign.eligible_games_count
    );
    const mb = campaign.minimum_bet;

    result.push({
      campaign,
      subtype: campaign.subtype,
      // Absent min-bet stays `undefined` (not a `{value:0}` sentinel), so a consumer
      // can branch on presence rather than only `value > 0`.
      minimum_bet: mb
        ? { value: mb.value, currency: mb.currency, display: mb.display }
        : undefined,
      prizes: campaign.prizes ?? [],
      tasks: campaign.tasks ?? [],
      eligible_games: games,
      eligible_games_count: count,
    });
  }

  return result;
}
